import { SpaceSelectCompassComponent } from "@circle-react-shared/SpaceSelectCompass/SpaceSelectCompassComponent";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { Modal } from "@circleco/compass/components/Modal";
import { Typography } from "@circleco/compass/components/Typography";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

import type { DuplicateEventModalProps } from ".";
import { useEventDuplicateMutation } from "./useEventDuplicateMutation";

export const CompassDuplicateEventModal = ({
  event,
  onClose,
  show,
}: DuplicateEventModalProps) => {
  const {
    isLoading,
    helpers: { spacesWithEventCreationAllowed },
  } = useSpacesContext();
  const { data: space } = useCurrentSpaceContext();
  const [selectedSpaceId, setSelectedSpaceId] = useState<
    number | null | undefined
  >(space?.id);
  const handleDuplicate = useEventDuplicateMutation(event);

  useEffect(() => {
    if (space?.id !== undefined && selectedSpaceId === undefined) {
      setSelectedSpaceId(space.id);
    }
  }, [space?.id, selectedSpaceId]);

  if (isLoading) return null;

  const spaces = spacesWithEventCreationAllowed();

  return (
    <Modal.Root
      open={show}
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Modal.Content>
        <Modal.Header
          title={t("duplicate_event_title", { event_title: event.name })}
        />
        <Modal.Body>
          <SpaceSelectCompassComponent
            spaces={spaces}
            value={selectedSpaceId ?? null}
            onChange={id => setSelectedSpaceId(id ?? null)}
            label={t("space")}
            description={t("duplicate_event_to_space")}
            aria-label={t("select_space")}
          />
          <Typography variant="caption" color="tertiary">
            {t("attendees_wont_be_duplicated")}
          </Typography>
        </Modal.Body>
        <Modal.Footer
          primaryAction={{
            label: t("duplicate"),
            onClick: () =>
              handleDuplicate.mutate({
                space_id: selectedSpaceId ?? undefined,
              }),
            disabled: !selectedSpaceId,
            loading: handleDuplicate.isPending,
          }}
          secondaryAction={{
            label: t("cancel"),
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
